import { extendObservable, action, computed } from 'mobx';
import { camelCase } from '@ridi/object-case-converter';
import axios from '../../../libs/axios_csrf';
import Conversation from '../models/Conversation';
import {
  CONVERSATION_TYPES,
  CHAT_DEFAULT_BODY,
  CHAT_DEFAULT_SUBJECT
} from '../constants';

export default class Conversations {
  constructor(rootStore, initialConversationId) {
    this.rootStore = rootStore;
    extendObservable(this, {
      conversations: [],
      loading: false,
      currentConversation: {}
    });
    this.firstFetch = false;
    // this.fetchIndex();

    // Id from last opened conversation as stored in the server
    // This variable is useful to recover the chat state after
    // a page reload
    this.initialConversationId = initialConversationId;

    // Action Cable
    this.fetchDataFromActionCable = this.fetchDataFromActionCable.bind(this);
    createChatSubscription(
      this.rootStore.user.id,
      this.fetchDataFromActionCable
    );
  }

  @computed
  get unreadConversationsCount() {
    return this.conversations.filter(c => c.unread).length;
  }

  @computed
  get sortedConversations() {
    return this.conversations.sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
  }

  @computed
  get initialConversation() {
    if (this._initialConversation) {
      return this._initialConversation;
    }
    this._initialConversation = this.findConversationById(
      this.initialConversationId
    );
    return this._initialConversation;
  }

  @action loadConversations(conversationsJson) {
    conversationsJson.forEach(c => this.loadConversation(c));
  }

  @action loadConversation(conversation) {
    const foundConversation = this.findConversationById(conversation.id);
    if (!foundConversation) {
      const newConversation = Conversation.deserialize(conversation, this);
      newConversation.id = conversation.id;
      const nextConversationIndex = this.conversations.push(newConversation);
      return this.conversations[nextConversationIndex - 1];
    }
    return foundConversation;
  }

  getPairConversationWithContact(contactId) {
    return this.conversations.find(conversation =>
      conversation.isPairConversationWith(contactId)
    );
  }

  @action showConversation(conversation) {
    this.setCurrentConversation(conversation);
    this.rootStore.uiStore.showConversation();
  }

  @action setCurrentConversation(conversation) {
    this.currentConversation = conversation;
  }

  @action create(subject, body, participants, type) {
    if (type == CONVERSATION_TYPES.GROUP) {
      this.createBackend(subject, CHAT_DEFAULT_BODY, participants);
    } else {
      this.createBackend(CHAT_DEFAULT_SUBJECT, body, participants);
    }
  }

  findConversationById(id) {
    return this.conversations.find(c => c.id == id);
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // BACKEND CONNECTIONS
  @action
  fetchIndex() {
    if (this.firstFetch) {
      return;
    }
    this.loading = true;
    axios
      .get('/conversations.json')
      .then(
        action(response => {
          const conversations = camelCase(response.data, true);
          const chatConversations = conversations.filter(
            conversation => conversation.isChat
          );
          this.loadConversations(chatConversations);
          this.loading = false;
          this.firstFetch = true;
          this.rootStore.uiStore.recoverLastChatState();
        })
      )
      .catch(
        action(error => {
          return null;
        })
      );
  }

  @action
  fetchConversation(conversation) {
    // this.setViewStateBeforeConversationFetch(conversationId);
    if (conversation.hasUnretrievedMessages) {
      this.loading = true;
      return axios
        .get('/conversations/' + conversation.id + '/receipts', {
          params: { page: conversation.messagesNextPage }
        })
        .then(
          action(response => {
            this.loading = false;
            return camelCase(response.data, true);
          })
        )
        .catch(
          action(error => {
            // Show list when there is an error
            // this.showConversationsList();
            console.log(error);
            return null;
          })
        );
    }

    return new Promise((resolve, reject) => resolve(null));
  }

  @action
  reply(conversation, message) {
    return axios
      .post('/conversations/' + conversation.id + '/reply.json', {
        body: message.body
      })
      .then(response => {
        return camelCase(response.data, true);
      })
      .catch(error => {
        return new Promise((resolve, reject) => reject(error));
      });
  }

  @action
  createBackend(subject, body, recipients) {
    axios
      .post('/messages.json', {
        recipients: recipients,
        from_chat: true,
        message: { body: body, subject: subject }
      })
      .then(
        action(response => {
          let conversationJson = camelCase(response.data, true);
          const conversation = this.loadConversation(conversationJson);
          conversation.openConversation();
        })
      )
      .catch(
        action(error => {
          console.log(error);
          return null;
        })
      );
  }

  /*
        onDeleteCurrentConversation(){
            let index = this.state.conversations.findIndex(
                (el) => el.id === this.state.currentConversationId
            );
            if( index != -1 ){
                if( this.state.conversations.length == 1 ){
                    this.showNewConversationForm();
                }
                else{
                    this.showConversationsList();
                }
                this.setState({
                    conversations: update( this.state.conversations,
                        {$splice: [[index, 1]] }
                    )
                });
                axios.delete('/conversations/' + this.state.currentConversationId + '.json')
                    .then(function (response) {
                        //console.log(response);
                    })
                    .catch(function (error) {
                        console.log("Error while deleting conversation");
                        console.log(error);
                    });
            }
        }
    */

  // end BACKEND CONNECTIONS
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ACTION CABLE

  // Called by ActionCable listener
  @action
  fetchDataFromActionCable(data) {
    if (data.message) {
      this.handleMessageFromActionCable(
        camelCase(JSON.parse(data.message), true)
      );
    }
    if (data.conversation) {
      this.handleConversationFromActionCable(
        camelCase(JSON.parse(data.conversation), true)
      );
    }
  }

  @action
  handleMessageFromActionCable(message) {
    // Add message to the conversation messages array if it comes from another user
    if (message.sender.id != this.rootStore.user.id) {
      const conversation = this.findConversationById(message.conversationId);
      if (conversation) {
        conversation.appendMessageFromActionCable(message);
        conversation.setLastSender(message.sender);
        conversation.setLastMessage(message);
      }
    }
  }

  @action
  handleConversationFromActionCable(conversation) {
    conversation.unread = true;
    if (!this.findConversationById(conversation.id)) {
      this.rootStore.uiStore.playNewConversationSound();
      this.loadConversation(conversation);
    }
  }
  // end ACTION CABLE
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
}
